import { ref, watch } from 'vue'
import apiClient, { getLoginToken } from '../api/axios.js'
import servoList from '../servoList.json'

// 设备列表与当前设备舵机型号管理
// 外部依赖：
//   selectedEnv - 当前环境对象 (Ref，需含 manager_url)
export function useDevice({ selectedEnv, tenantId }) {
  const deviceId = ref('')
  const deviceList = ref([])
  const deviceListLoading = ref(false)
  const currentServoList = ref([]) // 当前设备对应的舵机列表
  const servoVersion = ref('')

  // 根据设备型号(servoVersion)更新舵机列表
  // 仅当设备型号在 servoList 中有匹配项时才展示对应舵机；
  // 没有匹配型号则展示空列表，不再回退到默认舵机
  function updateServoListByDevice(device) {
    if (device && device.servoVersion && servoList[device.servoVersion]) {
      currentServoList.value = servoList[device.servoVersion]
    } else {
      currentServoList.value = []
    }
  }

  // 拉取设备列表
  function fetchDeviceList() {
    if (!selectedEnv.value.manager_url) return
    deviceListLoading.value = true

    const baseUrl = selectedEnv.value.manager_url
    const loginToken = getLoginToken()
    const rawLoginToken = loginToken.replace(/^Bearer\s+/i, '')
    const selectedTenantId = tenantId?.value
    const tenantParams = selectedTenantId !== null && selectedTenantId !== undefined && selectedTenantId !== ''
      ? { tenantId: selectedTenantId }
      : {}
    const debugDeviceConfig = {
      // Authorization 由拦截器继续注入当前环境的 server 密钥；
      // 登录 token 仅用于后端识别管理员身份和舵机操作权限。
      headers: rawLoginToken ? { 'X-User-Token': rawLoginToken } : {},
      params: tenantParams
    }
    const oauthConfig = {
      headers: loginToken ? { Authorization: loginToken } : {},
      params: tenantParams
    }
    // debug-devices 仅返回设备ID字符串数组；舵机型号和用户昵称需分别从状态、用户设备接口合并。
    Promise.all([
      apiClient.get(baseUrl + '/xiaozhi/device/status/debug-devices', debugDeviceConfig),
      apiClient.get(baseUrl + '/xiaozhi/device/status/all-devices').catch(() => ({ data: null })),
      loginToken
        ? apiClient.get(baseUrl + '/xiaozhi/device/list', {
            ...oauthConfig
          }).catch(() => ({ data: null }))
        : Promise.resolve({ data: null })
    ])
      .then(([debugRes, allRes, userDeviceRes]) => {
        // 构建设备型号和用户昵称映射。
        const servoVersionMap = {}
        const userNicknameMap = {}
        if (allRes && allRes.data && allRes.data.code === 0 && Array.isArray(allRes.data.data)) {
          allRes.data.data.forEach(device => {
            if (device && device.deviceId) {
              servoVersionMap[device.deviceId] = device.servoVersion
            }
          })
        }

        const userDevices = userDeviceRes?.data?.code === 0
          && Array.isArray(userDeviceRes.data?.data?.list)
          ? userDeviceRes.data.data.list
          : []
        userDevices.forEach(device => {
          const nickname = typeof device?.userNickName === 'string'
            ? device.userNickName.trim()
            : ''
          if (device?.id && nickname) {
            userNicknameMap[device.id] = nickname
          }
        })

        // 处理数据结构：data 为设备ID字符串数组
        if (debugRes.data && debugRes.data.code === 0 && Array.isArray(debugRes.data.data)) {
          // 提取设备ID列表，并合并对应的舵机型号与用户昵称。
          deviceList.value = debugRes.data.data.map(id => {
            const nickname = userNicknameMap[id]
            return {
              id,
              name: nickname ? `${id}（${nickname}）` : id,
              servoVersion: servoVersionMap[id] || '',
              userNickname: nickname || ''
            }
          })
          // 自动选择第一个设备
          if (deviceList.value.length > 0) {
            deviceId.value = deviceList.value[0].id
            servoVersion.value = deviceList.value[0].servoVersion || ''
            // 根据 servoVersion 更新舵机列表
            updateServoListByDevice(deviceList.value[0])
          }
        } else {
          deviceList.value = []
        }
      })
      .catch(() => {
        deviceList.value = []
      })
      .finally(() => {
        deviceListLoading.value = false
      })
  }

  // 获取当前选中设备的舵机型号（供轨迹/镜像等逻辑使用）
  function getSelectedDeviceServoVersion() {
    if (deviceId.value) {
      const d = deviceList.value.find(x => x.id === deviceId.value)
      if (d?.servoVersion && servoList[d.servoVersion]) return d.servoVersion
    }
    if (servoVersion.value && servoList[servoVersion.value]) return servoVersion.value
    return ''
  }

  // 监听设备选择变化，更新舵机列表
  watch(deviceId, (newDeviceId) => {
    if (newDeviceId) {
      const selectedDevice = deviceList.value.find(device => device.id === newDeviceId)
      if (selectedDevice) {
        servoVersion.value = selectedDevice.servoVersion || ''
        updateServoListByDevice(selectedDevice)
      }
    } else {
      servoVersion.value = ''
      currentServoList.value = []
    }
  })

  return {
    deviceId,
    deviceList,
    deviceListLoading,
    currentServoList,
    servoVersion,
    fetchDeviceList,
    updateServoListByDevice,
    getSelectedDeviceServoVersion
  }
}
